// src/App.jsx
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Signup from "./auth/Signup";
import Login from "./auth/Login";
import ImpersonateVerify from "./auth/ImpersonateVerify";
import AcceptInvite from "./auth/AcceptInvite";
import DirectLogin from "./auth/DirectLogin";
import Register from "./pages/Register";
import Personnel from "./admin/Personnel";
import Dashboard from "./admin/Dashboard";
import Clients from "./admin/Clients";
import Accounting from "./admin/Accounting";
import MyAccount from "./admin/MyAccount";
import Accounts from "./admin/Admin_Accounts";
import Account from "./admin/Accounts";
import Settings from "./admin/Admin_Settings";
import Messages from "./admin/Admin_Messages";
import Audits from "./admin/Admin_Audits";
import Jobs from "./admin/Jobs";
import Schedule from "./admin/Schedule";
import AdminTimesheets from "./admin/AdminTimesheets";
import TimesheetPrint from "./admin/TimesheetPrint";
import ForgotPassword from "./auth/ForgotPassword";
import ResetPassword from "./auth/ResetPassword";
import ChangePassword from "./auth/ChangePassword";
import Profile from "./auth/Profile";
import ItemDetail from "./pages/ItemDetail";
import NotFound from "./pages/NotFound";
import Availability from "./tempStaff/pages/Availability";
import AssignedTask from "./tempStaff/pages/AssignedTask";
import Earnings from "./tempStaff/pages/Earnings";
import MyAccount_tempStaff from "./tempStaff/pages/MyAccount_tempStaff";
import ClientDashboard from "./clientReal/ClientDashboard";
import ClientShiftsSchedule from "./clientReal/ClientShiftsSchedule";
import ClientShiftsTimesheets from "./clientReal/ClientShiftsTimesheets";
import ClientMyAccount from "./clientReal/ClientMyAccount";
import ClientFiles from "./clientReal/ClientFiles";
import ProtectedRoute from "./ProtectedRoute";
import ProtectedClientRoute from "./components/ProtectedClientRoute";
import CheckInHome from "./checkin/CheckInHome";
import FaceScan from "./checkin/FaceScan";
import FaceRecognized from "./checkin/FaceRecognized";
import FaceNotRecognized from "./checkin/FaceNotRecognized";
import VerifyPhone from "./checkin/VerifyPhone";
import Help from "./checkin/Help";
import Manage from "./checkin/Manage";
import ManagePassword from "./checkin/ManagePassword";
import ManageDashboard from "./checkin/ManageDashboard";
import ManageDevice from "./checkin/ManageDevice";
import ManageSignoff from "./checkin/ManageSignoff";
import QRNoShift from "./checkin/QRNoShift";
import QRSuccess from "./checkin/QRSuccess";
import QRFaceNotIdentified from "./checkin/QRFaceNotIdentified";
import CheckInSuccess from "./checkin/CheckInSuccess";
import ShiftDetails from "./checkin/ShiftDetails";
import DeviceHome from "./checkin/DeviceHome";
import DeviceSignin from "./checkin/DeviceSignin";
import DeviceAssign from "./checkin/DeviceAssign";
import FaceRecognitionTest from "./admin/FaceRecognitionTest";
import useAuth from "./hooks/useAuth";
import FloatingExitImpersonation from './components/FloatingExitImpersonation';
import { PermissionProvider } from "./context/PermissionContext";
import { AppTimezoneProvider } from "./context/AppTimezoneContext";
import { loadStoredManageContextRaw } from "./checkin/manageContext";
import { loadManageSession } from "./checkin/manageSession";

function ManageRedirect() {
  const session = loadManageSession();
  const storedContext = loadStoredManageContextRaw();
  const hasLoginOrDevice = !!(session?.token || storedContext?.clientDeviceId);

  if (hasLoginOrDevice) {
    return <Navigate to="/manage/signin" replace />;
  } else {
    return <Navigate to="/device/signin" replace />;
  }
}

// /Kudos external redirect -> atlas-crm-demo.netlify.app
function KudosRedirect() {
  useEffect(() => {
    window.location.replace("https://atlas-crm-demo.netlify.app/");
  }, []);
  return null;
}
//test pr comment
//test deployement test
export default function App() {
  // Cleanup invalid tokens on app startup
  // BUT: Don't run this cleanup if we're in the middle of exiting impersonation
  // (check for a flag or delay the cleanup)
  useEffect(() => {
    // Delay cleanup slightly to avoid interfering with exit impersonation
    const cleanupTimer = setTimeout(() => {
      const token = localStorage.getItem('token');
      if (token === 'null' || token === 'undefined' || (token && token.trim() === '')) {
        console.warn('App: Removing invalid token from localStorage', { token });
        localStorage.removeItem('token');
        localStorage.removeItem('accountId');
        localStorage.removeItem('isAdmin');
        localStorage.removeItem('userType');
        localStorage.removeItem('registrationComplete');
        window.dispatchEvent(new Event('authUpdate'));
      }
    }, 1000); // Delay by 1 second to avoid interfering with exit impersonation

    return () => clearTimeout(cleanupTimer);
  }, []);
//test comment
  // Initialize global auth listeners and timers (refresh & idle logout)
  const auth = useAuth();
  return (
    <BrowserRouter>
      <AppTimezoneProvider>
      <PermissionProvider>
      <FloatingExitImpersonation />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/impersonate-verify" element={<ImpersonateVerify />} />
        <Route path="/accept-invite/:guid" element={<AcceptInvite />} />
        <Route path="/direct-login/:guid" element={<DirectLogin />} />
        <Route path="/notfound" element={<NotFound />} />
        <Route path="/Kudos" element={<KudosRedirect />} />

        {/* Check-in routes - NO authentication required (face recognition is the identity mechanism) */}
        <Route path="/checkin" element={<CheckInHome />} />
        <Route path="/device" element={<DeviceHome />} />
        <Route path="/device/signin" element={<DeviceSignin />} />
        <Route path="/device/assign" element={<DeviceAssign />} />
        <Route path="/checkin/scan" element={<FaceScan />} />
        <Route path="/checkin/face-recognized" element={<FaceRecognized />} />
        <Route path="/checkin/face-not-recognized" element={<FaceNotRecognized />} />
        <Route path="/checkin/verify-phone" element={<VerifyPhone />} />
        <Route path="/checkin/help" element={<Help />} />
        <Route path="/manage/signin" element={<Manage />} />
        <Route path="/manage/password" element={<ManagePassword />} />
        <Route path="/manage/dashboard" element={<ManageDashboard />} />
        <Route path="/manage/device" element={<ManageDevice />} />
        <Route path="/manage/signoff" element={<ManageSignoff />} />
        <Route path="/manage" element={<ManageRedirect />} />
        <Route path="/checkin/manage" element={<Navigate to="/manage/signin" replace />} />
        <Route path="/checkin/success" element={<CheckInSuccess />} />

        {/* QR Check-in routes - No authentication required */}
        <Route path="/checkin/shift-details" element={<ShiftDetails />} />
        <Route path="/checkin/qr-no-shift" element={<QRNoShift />} />
        <Route path="/checkin/qr-success" element={<QRSuccess />} />
        <Route path="/checkin/qr-face-not-identified" element={<QRFaceNotIdentified />} />

        {/* temp staff routes */}
        <Route path="/availability" element={
          <ProtectedRoute>
            <Availability />
          </ProtectedRoute>
        } />
        <Route path="/assignedTask" element={
          <ProtectedRoute>
            <AssignedTask />
          </ProtectedRoute>
        } />
        <Route path="/earnings" element={
          <ProtectedRoute>
            <Earnings />
          </ProtectedRoute>
        } />
        <Route path="/myaccount-tempstaff" element={
          <ProtectedRoute>
            <MyAccount_tempStaff />
          </ProtectedRoute>
        } />

        <Route path="/register" element={
          <ProtectedRoute>
            <Register />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute adminOnly={true}>
            <Dashboard />
          </ProtectedRoute>
        } />
        <Route path="/personnel" element={
          <ProtectedRoute adminOnly={true} requiredModule="personnel">
            <Personnel />
          </ProtectedRoute>
        } />
        <Route path="/clients" element={
          <ProtectedRoute adminOnly={true} requiredModule="client">
            <Clients />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={
          <ProtectedRoute adminOnly={true} requiredModule="shift">
            <Jobs />
          </ProtectedRoute>
        } />
        <Route path="/schedule" element={
          <ProtectedRoute adminOnly={true} requiredModule="shift">
            <Schedule />
          </ProtectedRoute>
        } />
        <Route path="/admin/timesheets" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <AdminTimesheets />
          </ProtectedRoute>
        } />
        <Route path="/admin/timesheets/print" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <TimesheetPrint />
          </ProtectedRoute>
        } />
        <Route path="/accounting" element={
          <ProtectedRoute adminOnly={true} requiredModule="accounting">
            <Accounting />
          </ProtectedRoute>
        } />
        <Route path="/my-account" element={
          <ProtectedRoute adminOnly={true}>
            <MyAccount />
          </ProtectedRoute>
        } />
        <Route path="/client-dashboard/my-account" element={
          <ProtectedClientRoute>
            <ClientMyAccount />
          </ProtectedClientRoute>
        } />
        <Route path="/client-dashboard/files" element={
          <ProtectedClientRoute>
            <ClientFiles />
          </ProtectedClientRoute>
        } />
        <Route path="/client-dashboard/shifts/timesheets" element={
          <ProtectedClientRoute>
            <ClientShiftsTimesheets />
          </ProtectedClientRoute>
        } />
        <Route path="/client-dashboard/shifts/schedule" element={
          <ProtectedClientRoute>
            <ClientShiftsSchedule />
          </ProtectedClientRoute>
        } />
        <Route path="/client-dashboard/shifts" element={
          <Navigate to="/client-dashboard/shifts/schedule" replace />
        } />
        <Route path="/client-dashboard" element={
          <ProtectedClientRoute>
            <ClientDashboard />
          </ProtectedClientRoute>
        } />
        <Route path="/admin/accounts" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <Accounts />
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <Account />
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/admin/messages" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <Messages />
          </ProtectedRoute>
        } />
        <Route path="/admin/audits" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <Audits />
          </ProtectedRoute>
        } />
        <Route path="/admin/face-recognition-test" element={
          <ProtectedRoute adminOnly={true} requiredModule="admin">
            <FaceRecognitionTest />
          </ProtectedRoute>
        } />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/register/:itemId" element={
          <ProtectedRoute>
            <ItemDetail />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/signup" replace />} />
      </Routes>
      </PermissionProvider>
      </AppTimezoneProvider>
    </BrowserRouter>
  );
}
